import type { RequestParameters } from 'relay-runtime';

type PromiseGetter<ResponseType> = () => Promise<ResponseType>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface RetryOptions<ResponseType = any> {
	retryFunc: PromiseGetter<ResponseType>;
	retryPredicate?: (error: Error) => boolean;
	retryAttempts?: number;
	onRetry?: (error: Error, retryAttempt: number) => void;
}

export const RETRY_ATTEMPTS_LIMIT = 1;

/**
 * Facilitates a customizable retry strategy for asynchronous operations prone to transient failures.
 * It allows specifying conditions under which retries should occur, the maximum number of retries,
 * and an optional callback for each retry attempt, thereby enhancing the operation's robustness.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const retryOnError = <ResponseType = any,>(
	initialPromiseGetter: PromiseGetter<ResponseType>,
	{
		retryFunc,
		retryPredicate,
		retryAttempts = RETRY_ATTEMPTS_LIMIT,
		onRetry,
	}: RetryOptions<ResponseType>,
): Promise<ResponseType> => {
	// Eventually we might like to make max attempts configurable.
	// To do so, we'll need to support jitter & exponential back-off
	const maxAttempts = retryAttempts > RETRY_ATTEMPTS_LIMIT ? RETRY_ATTEMPTS_LIMIT : retryAttempts;
	const execute = async (
		getPromise: PromiseGetter<ResponseType>,
		retryAttempt = 0,
	): Promise<ResponseType> => {
		try {
			return await getPromise();
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			// If no retry predicate is passed, retry on every error
			const shouldAttemptRetry = retryPredicate ? retryPredicate(error) : true;
			if (retryAttempt < maxAttempts && shouldAttemptRetry) {
				onRetry && onRetry(error, retryAttempt + 1);
				return execute(retryFunc, retryAttempt + 1);
			}
			throw error;
		}
	};
	return execute(initialPromiseGetter);
};

export const shouldRetry = (error: Error): boolean => {
	if ('statusCode' in error && typeof error.statusCode === 'number') {
		return [502, 503, 504].includes(error.statusCode);
	}
	return false;
};

export const getRetryAttemptsForQuery = (request: RequestParameters): 1 | 0 => {
	if (request.operationKind === 'query') {
		return RETRY_ATTEMPTS_LIMIT;
	}

	return 0;
};
